import IdleTimer from 'exacta-material-react/lib/src/components/IdleTimer';
import isElectron from 'is-electron';
import { TargettedSystems } from 'nots-window-renderer';
import RoleStatus from 'nots-window-renderer/lib/Catiz3/RoleStatus';
import QueryString from 'query-string';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import styled from 'styled-components';

import LoadingProgress from '../../components/LoadingProgess';
import System from '../../config/SystemConfig';
import { CONTEXT_PATH } from '../../env/variables';
import {
  CatizDisplayLayoutModes,
  CatizMonitors,
  CatizTabs,
  DisplayConstants,
  IdleTimeout,
  NotsSystem,
} from '../../helpers/constants';
import useLessonReplay from '../../hooks/useLessonReplay';
import useResource from '../../hooks/useResource';
import useSystemReadyWithMonitors from '../../hooks/useSystemReadyWithMonitors';
import {
  setPlayerMode,
  setPlayerRole,
} from '../../store/actions/resources.actions';
import { RootState } from '../../types/rootState';
import { lessonControllerHeight } from '../../utils/display.util';
import { fileResourceUrl } from '../../utils/urlUtils';
import { DemoInteractionBlocker } from '../common-components/DemoInteractionBlocker';
import GuidedContainer from '../common-components/GuidedContainer';
import useLogger from '../common-components/hooks/useLogger';
import ImageContainer from '../common-components/ImageContainer';
import Map from '../common-components/Map';
import MapSizeContext from '../common-components/MapSizeContext';
import { MultimediaInteractionBlocker } from '../common-components/MultimediaBlocker';
import { PauseOverlay } from '../common-components/PauseOverlay';
import StartLesson from '../common-components/StartLesson';
import TracksOnImage from '../common-components/TracksOnImage';
import WindowDisplayManager from '../common-components/WindowDisplayManager';
import MultimediaDialog from '../MultimediaDialog';
import MonitorContext from '../S80/MonitorContext';
import AlertPanel from './AlertPanel';
import AlphaRVideoArea from './AlphaRVideoArea';
import CatizMenu from './CatizMenu';
import { borderColor, grayBorder } from './Colors';
import HooksPanel from './HooksPanel';
import MenuItems from './MenuItems.json';
import * as CatSizes from './sizes';
import TabPanel from './TabPanel';
import TacsitInformationArea from './TacsitInformationArea';
import { VideoContentTab } from './VideoTabPanel';
import WindowHeader from './WindowHeader';

const Container = styled.div`
  background: rgba(32, 41, 68, 1);
  color: rgba(231, 221, 30, 1);
  width: ${CatSizes.OuterContainer.width}px;
  height: ${CatSizes.OuterContainer.height}px;
  display: grid;
  grid-template-rows: ${CatSizes.MenuBarArea.height}px ${CatSizes.TACSITArea
      .height + CatSizes.AlertArea.height}px ${CatSizes.NotificationArea
      .height}px;
`;

const SystemStatusLine = styled.div<any>`
  border: solid 1px ${borderColor};
  animation: ${(props) =>
    props.showBlinking ? 'blink 2s linear infinite' : ''};
  @keyframes blink {
    0% {
      color: inherit;
      background-color: inherit;
    }
    50% {
      color: black;
      background-color: orange;
    }
    100% {
      color: inherit;
      background-color: inherit;
    }
  }
`;

const MiddlePane = styled.div`
  display: grid;
  grid-template-rows: auto ${CatSizes.AlertArea.height}px;
`;

const MainPane = styled.div`
  display: grid;
  grid-template-columns: ${CatSizes.WindowArea.width}px ${CatSizes.TACSITArea
      .width}px ${2 * CatSizes.PrimaryCloseControlArea.width}px;
`;

const AlertAndStatusPane = styled.div`
  display: grid;
  grid-template-columns: ${CatSizes.AlertArea.width}px ${CatSizes.TabPanelArea
      .width}px;
`;

const WindowPane = styled.div`
  ${grayBorder}
`;

const TacsitPane = styled.div`
  ${grayBorder};
  position: relative;
`;

const RightPane = styled.div`
  ${grayBorder}
  display: grid;
  grid-template-rows: ${CatSizes.PrimaryCloseControlArea.height}px ${CatSizes
      .RoleArea.height}px;
`;

const HooksPane = styled.div`
  ${grayBorder}
`;
const RoleStatusPane = styled.div`
  ${grayBorder}
  height: 100%;
`;

const AlertPane = styled.div`
  ${grayBorder}
`;
const StatusPane = styled.div`
  ${grayBorder}
`;

const CatizMapSizes = {
  width: 1570,
  height: 1362,
};

const GFCS2MapSize = {
  width: CatSizes.GfcsMapArea.width,
  height: 1362,
};

const GFCS1MapSize = {
  width: CatSizes.GfcsMapArea.width,
  height: 1362 - CatSizes.AlphaRVideoArea.height,
};

const GfcsMainPane = styled.div`
  display: grid;
  grid-template-columns: ${CatSizes.GfcsMapArea.width}px auto ${CatSizes
      .WindowArea.width}px;
`;

const GfcsTacsitPane = styled.div<any>`
  ${grayBorder}
  display: grid;
  grid-template-rows: ${(props) =>
    props.layoutMode === CatizDisplayLayoutModes.GFCS1
      ? `auto ${CatSizes.AlphaRVideoArea.height}px ${CatSizes.AlertArea.height}px;`
      : `auto ${CatSizes.AlertArea.height}px;`};
`;

const GfcsRightPane = styled.div`
  ${grayBorder}
  display: grid;
  grid-template-rows: ${CatSizes.PrimaryCloseControlArea.height}px auto;
`;
const GfcsMiddlePane = styled.div`
  display: grid;
  grid-template-rows: 24px auto;
`;

const GfcsVideoPane = styled.div`
  display: grid;
  grid-template-rows: 50% 50%;
`;

const GfcsRoleStausArea = styled.div`
  position: absolute;
  width: ${CatSizes.WindowArea.width}px;
`;

export default function Catiz3() {
  const logger = useLogger('Catiz3/index.tsx', 'Catiz Main Tab');
  const location = useLocation();
  const dispatch = useDispatch();
  const {
    mode,
    role,
    system,
    unit,
    token,
    version,
    enrollId,
    attemptType,
    subjectId,
  } = QueryString.parse(location.search);
  const { systemReady, resetSystem } = useSystemReadyWithMonitors(2);
  const { t } = useTranslation();
  const generalDisplayAreaRef = useRef(null);
  useEffect(() => {
    System.setSystem(NotsSystem.CATIZ).setCurrentTab(CatizTabs.MAIN);
  }, []);
  useLessonReplay(
    {
      system: `${system}`,
      unit: `${unit}`,
      token: `${token}`,
      version: `${version}`,
      mode: `${mode}`,
      role: `${role}`,
      enrollId: `${enrollId}`,
      attemptType: `${attemptType}`,
      subjectId: `${subjectId}`,
    },
    resetSystem
  );
  const [showTutorialBlinking, setShowTutorialBlinking] =
    useState<boolean>(false);
  const { roleStatus, tutorialArea, mainMonitorLayoutMode } = useSelector(
    (state: any) => state.executor.catiz
  );
  useEffect(() => {
    setShowTutorialBlinking((prevValue) => true);
    setTimeout(() => setShowTutorialBlinking(false), 6000);
  }, [tutorialArea.term]);
  useEffect(() => {
    logger.log('Catiz starting');
    if (location.pathname === '/catiz3/main') {
      //Added a 2 seconds delay before opening the other monitors, Will remove once the resources loading isssue is solved
      if (!isElectron()) {
        setTimeout(() => {
          window.open(
            `${window.location.origin}${CONTEXT_PATH}/app/nots_player/catiz3/touchpanel?mode=${mode}`,
            'Desk',
            `resizable=off,left=400,top=400,width=1280,height=800`
          );

          window.open(
            `${window.location.origin}${CONTEXT_PATH}/app/nots_player/lesson-controller?mode=${mode}&system=catiz3`,
            `Lesson Controller`,
            `resizable=false,left=1600,top=200,width=400,height=${lessonControllerHeight(
              mode,
              'catiz3'
            )}`
          );
        }, 2000);
      }
    }
  }, [location.pathname]);
  useEffect(() => {
    dispatch(setPlayerMode(mode));
    dispatch(setPlayerRole(role));
  }, [mode, role, dispatch]);

  if (!systemReady) {
    return <LoadingProgress message={t('loadingProgress.screenLoad')} />;
  }
  return (
    <Container>
      <MonitorContext.Provider
        value={{
          monitor: CatizMonitors.MainMapArea,
        }}
      >
        <MapSizeContext.Provider value={CatizMapSizes}>
          <CatizMenu menuItems={MenuItems} />
          <StartLesson preferredDialogMonitor={CatizMonitors.MainMapArea} />
          <PauseOverlay />
          <DemoInteractionBlocker />
          <MultimediaInteractionBlocker />
          <IdleTimer timeout={IdleTimeout} />
          <GuidedContainer data-area="guided-container" />
          {mainMonitorLayoutMode === CatizDisplayLayoutModes.GFCS1 ||
          mainMonitorLayoutMode === CatizDisplayLayoutModes.GFCS2 ? (
            <GfcsLayout
              generalDisplayAreaRef={generalDisplayAreaRef}
              roleStatus={roleStatus}
              layoutMode={mainMonitorLayoutMode}
            />
          ) : (
            <CmsLayout
              generalDisplayAreaRef={generalDisplayAreaRef}
              roleStatus={roleStatus}
            />
          )}
          {tutorialArea.term && (
            <SystemStatusLine showBlinking={showTutorialBlinking}>
              {tutorialArea?.term ?? ''}
            </SystemStatusLine>
          )}
        </MapSizeContext.Provider>
      </MonitorContext.Provider>
    </Container>
  );
}

function CmsLayout(props) {
  const { generalDisplayAreaRef, roleStatus } = props;
  return (
    <MiddlePane>
      <MainPane>
        <WindowPane>
          <WindowDisplayManager
            area={DisplayConstants.GeneralDisplayArea}
            targetSystem={TargettedSystems.CATIZ}
          />
        </WindowPane>
        <TacsitPane data-area="tacsit-area">
          <Map
            svgHeight={CatizMapSizes.height + ''}
            svgWidth={CatizMapSizes.width + ''}
            monitor={CatizMonitors.MainMapArea}
          />
          <ImageContainer monitor={DisplayConstants.UpperMonitor} />
          <TracksOnImage />
          <MultimediaDialog />
          <TacsitInformationArea
            areaRef={generalDisplayAreaRef}
            data-area="common-info-area"
          />
        </TacsitPane>
        <RightPane>
          <HooksPanel />
          <RoleStatusPane>
            <RoleStatus content={roleStatus} />
          </RoleStatusPane>
        </RightPane>
      </MainPane>
      <AlertAndStatusPane>
        <AlertPanel />
        <TabPanel />
      </AlertAndStatusPane>
    </MiddlePane>
  );
}

function GfcsLayout(props) {
  const { generalDisplayAreaRef, roleStatus, layoutMode } = props;
  const auxiliarVideo = useSelector(
    (state: any) => state.executor.catiz.auxiliarVideo
  );
  const processedVideo = useSelector(
    (state: any) => state.executor.catiz.processedVideo
  );
  const auxiliarVideoResource = useResource(auxiliarVideo.asset);
  const processedVideoResource = useResource(processedVideo.asset);
  console.log({ auxiliarVideoResource, processedVideoResource });
  const gfcsMapSizes = ((layoutMode: string) => {
    return layoutMode === CatizDisplayLayoutModes.GFCS2
      ? GFCS2MapSize
      : GFCS1MapSize;
  })(layoutMode);
  return (
    <GfcsMainPane>
      <MapSizeContext.Provider value={gfcsMapSizes}>
        <MonitorContext.Provider
          value={{
            monitor: CatizMonitors.MainMapArea,
          }}
        >
          <GfcsTacsitPane layoutMode={layoutMode}>
            <TacsitPane data-area="tacsit-area">
              <Map
                svgHeight={CatizMapSizes.height + ''}
                svgWidth={CatSizes.GfcsMapArea.width + ''}
                monitor={CatizMonitors.MainMapArea}
              />
              <ImageContainer monitor={DisplayConstants.UpperMonitor} />
              <TracksOnImage />
              <MultimediaDialog />
              <TacsitInformationArea
                areaRef={generalDisplayAreaRef}
                data-area="common-info-area"
              />
            </TacsitPane>
            {layoutMode === CatizDisplayLayoutModes.GFCS1 && (
              <AlphaRVideoArea />
            )}
            <AlertPanel />
          </GfcsTacsitPane>
          <GfcsMiddlePane>
            <WindowHeader name={'GFCS Video'} />
            <GfcsVideoPane>
              <VideoContentTab
                tabHeaders={[
                  {
                    label: 'B-Scope Video',
                    id: 'b_scope_video',
                    type: 'image',
                  },
                  {
                    label: 'Auxiliar Video',
                    id: 'auxiliar_video',
                    type: 'video',
                    url: auxiliarVideo ? auxiliarVideoResource : '',
                  },
                ]}
                defaultActiveTab="auxiliar_video"
                videoControls={false}
              />
              <VideoContentTab
                tabHeaders={[
                  {
                    label: 'Processed Video',
                    id: 'processed_video',
                    type: 'video',
                    url: processedVideoResource,
                  },
                ]}
                videoControls={false}
              />
            </GfcsVideoPane>
          </GfcsMiddlePane>
          <GfcsRightPane>
            <HooksPanel />
            <WindowDisplayManager
              area={DisplayConstants.DataEntryArea}
              targetSystem={TargettedSystems.CATIZ}
              zIndex={335} // Put this above the default window manager
            />
            <RoleStatusPane>
              <GfcsRoleStausArea>
                <RoleStatus content={roleStatus} />
              </GfcsRoleStausArea>
              <WindowDisplayManager
                area={DisplayConstants.GeneralDisplayArea}
                targetSystem={TargettedSystems.CATIZ}
                maxHeight={810}
                zIndex={333}
              />
            </RoleStatusPane>
          </GfcsRightPane>
        </MonitorContext.Provider>
      </MapSizeContext.Provider>
    </GfcsMainPane>
  );
}
